import { defineStore } from 'pinia'
import { loadUser, logoutUser } from '../services/api/user'
import { getLogoutRedirectUrl } from '@/utils/onboarding'
import router from '../router'
import type { User, UserRole as Role } from '@/services/api/types/user'
import { Driver, DriverCar } from '@/services/api/types/driver'
import { DriverApplication } from '@/services/api/types/driver-application'
import { listAllDriverApplications } from '@/services/api/driver-application'
import { getMyCars, getMyDriverProfile } from '@/services/api/driver'

interface GlobalState {
  isSidebarMinimized: boolean
  userName: string
  user: User
  userRole: string
  myDriverProfile?: Driver
  myCar?: DriverCar
  pendingDriverApplications: DriverApplication[]
  pendingDriverApplicationsCount: number
  pendingReviewApplicationsCount: number
  pendingDriverApplicationsLoaded: boolean
  pendingDriverApplicationsLoading: boolean
}

const roleOptions = [
  {
    label: 'Owner',
    value: 'owner',
    key: 'is_owner',
  },
  {
    label: 'Admin',
    value: 'admin',
    key: 'is_admin',
  },
  {
    label: 'Background checker',
    value: 'background_checker',
    key: 'is_background_checker',
  },

  {
    label: 'Ride support',
    value: 'ride_support',
    key: 'is_ride_support',
  },

  {
    label: 'Business partner',
    value: 'business_partner',
    key: 'is_business_partner',
  },
  {
    label: 'Broker',
    value: 'broker',
    key: 'is_broker',
  },

  {
    label: 'Driver',
    value: 'driver',
    key: 'is_driver',
  },

  {
    label: 'Supervisor',
    value: 'supervisor',
    key: 'is_supervisor',
  },
]

export const useGlobalStore = defineStore('global', {
  state: () => {
    return {
      isSidebarMinimized: false,
      userName: '',
      user: {} as User,
      myDriverProfile: {} as Driver,
      myCar: {} as DriverCar,
      userRole: sessionStorage.getItem('userRole') || '',
      pendingDriverApplications: [] as DriverApplication[],
      pendingDriverApplicationsCount: 0,
      pendingReviewApplicationsCount: 0,
      pendingDriverApplicationsLoaded: false,
      pendingDriverApplicationsLoading: false,
    }
  },

  getters: {
    defaultRole() {
      if (this.user.is_owner) return 'owner'
      else if (this.user.is_admin) return 'admin'
      else if (this.user.is_supervisor) return 'supervisor'
      else if (this.user.is_ride_support) return 'ride_support'
      else if (this.user.is_driver) return 'driver'
      else if (this.user.is_business_partner) return 'business_partner'
      else if (this.user.is_broker) return 'broker'
      else if (this.user.is_background_checker) return 'background_checker'
      return null
    },
  },

  actions: {
    toggleSidebar() {
      this.isSidebarMinimized = !this.isSidebarMinimized
    },

    changeUserName(userName: string) {
      this.userName = userName
    },

    setUserRole() {
      const role: Role = sessionStorage.getItem('userRole') as Role
      if (role) {
        this.userRole = role
      } else {
        if (this.defaultRole && !sessionStorage.getItem('userRole')) {
          this.userRole = this.defaultRole as unknown as string
          sessionStorage.setItem('userRole', this.defaultRole as string)
        } else if (!this.defaultRole) {
          sessionStorage.removeItem('userRole')
        }
      }
    },

    async getUser() {
      try {
        const { data } = await loadUser()
        this.userName = data.first_name ? `${data.first_name} ${data.last_name || ''}` : data.username
        this.user = data as User
        this.setUserRole()
        return data
      } catch {
        router.push('/auth/login')
      }
    },

    async logout() {
      try {
        await logoutUser()
      } catch {
        // Proceed even if the session is already invalid
      }
      sessionStorage.removeItem('userRole')
      localStorage.removeItem('userRole')
      localStorage.removeItem('completed_ride')
      this.user = {} as User
      this.userName = ''
      this.userRole = ''
      this.myDriverProfile = {} as Driver
      this.myCar = {} as DriverCar
      window.location.href = getLogoutRedirectUrl()
    },

    async getMyDriver() {
      try {
        const response = await getMyDriverProfile()
        this.myDriverProfile = response.data as Driver
        return response.data
      } catch (err) {
        console.log(err)
      }
    },

    async getMyCar() {
      try {
        const response = await getMyCars()
        this.myCar = response.data as DriverCar
        return response.data
      } catch (err) {
        console.log(err)
      }
    },

    async loadPendingDriverApplications(force = false) {
      if (this.pendingDriverApplicationsLoading) return
      if (this.pendingDriverApplicationsLoaded && !force) return
      try {
        this.pendingDriverApplicationsLoading = true
        const res = await listAllDriverApplications({ offset: 0, limit: 1000 })
        const items = res.data.items || []
        const pendingApplications = items.filter((item) => !item.rejected_on && !item.approved_on)
        const pendingReviewApplications = pendingApplications.filter(
          (item) => item.is_ready_for_review || item.is_sent_for_review,
        )
        this.pendingDriverApplications = pendingApplications
        this.pendingDriverApplicationsCount = pendingApplications.length
        this.pendingReviewApplicationsCount = pendingReviewApplications.length
        this.pendingDriverApplicationsLoaded = true
      } catch (err) {
        console.error('Error loading pending driver applications:', err)
        this.pendingDriverApplications = []
        this.pendingDriverApplicationsCount = 0
        this.pendingReviewApplicationsCount = 0
        this.pendingDriverApplicationsLoaded = false
      } finally {
        this.pendingDriverApplicationsLoading = false
      }
    },
  },
})
